import { Switch, Route, Redirect, useLocation, Link } from "wouter";
import { queryClient } from "./lib/queryClient";
import { PersistQueryClientProvider } from "@tanstack/react-query-persist-client";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
// force full reload — modal implementation 2026-03-19
import { SidebarProvider } from "@/components/ui/sidebar";
import { AppSidebar, navItems as sidebarNavItems, isNavItemVisible } from "@/components/app-sidebar";
import { UserProvider } from "@/lib/user-context";
import { SPECIALTY_PAGE_ACCESS } from "@shared/page-access";
import productionHubLogo from "@assets/Production_Hub_1771339483036.png";
import { ClipboardList, CalendarDays, LayoutDashboard, Columns3, Settings2, Users, Settings, Menu, X, LogOut, Bell, AtSign, Zap, Check, CheckCheck, Droplets, ChevronLeft, ChevronRight, Bug, Search } from "lucide-react";
import { MeshScreenIcon, PrintingPressIcon } from "@/lib/milestone-icons";
import { Fragment, useState, useEffect, useRef, lazy, Suspense } from "react";
import { useUser } from "@/lib/user-context";
import { useQuery, useMutation } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { qcNotificationQuery, readableNotificationData } from "@/lib/qc-notification-query";
import type { Notification } from "@shared/schema";
import { ReprintAlertBanner } from "@/components/reprint-alert-banner";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import BugReportDialog from "@/components/bug-report-dialog";
import { ErrorBoundary } from "@/components/error-boundary";
import { ChangePasswordDialog } from "@/components/change-password-dialog";
import { ImpersonationBanner } from "@/components/impersonation-banner";
import { IdentityRecovery } from "@/components/identity-recovery";
import { CommandSearch } from "@/components/command-search";
import { getAppHistoryState, initializeAppHistory, recordAppBack, recordAppForward, recordAppPop, recordAppPush, recordAppReplace } from "@/lib/navigation-context";
import { createShopFloorPersister, resumePausedShopFloorMutations, SHOP_FLOOR_PERSIST_BUSTER } from "@/lib/shop-floor-offline";

// Static imports — needed immediately or tiny
import NotFound from "@/pages/not-found";
import Login from "@/pages/login";
import ResetPassword from "@/pages/reset-password";

// Lazy-loaded route pages
const Dashboard = lazy(() => import("@/pages/dashboard"));
const WorkOrders = lazy(() => import("@/pages/work-orders"));
const WorkOrderDetail = lazy(() => import("@/pages/work-order-detail"));
const Schedule = lazy(() => import("@/pages/schedule"));
const Machines = lazy(() => import("@/pages/machines"));
const Lanes = lazy(() => import("@/pages/lanes"));
const Team = lazy(() => import("@/pages/team"));
const SettingsPage = lazy(() => import("@/pages/settings"));
const WebhookFields = lazy(() => import("@/pages/webhook-fields"));
const ScreenMaster = lazy(() => import("@/pages/screenmaster"));
const InkMaster = lazy(() => import("@/pages/inkmaster"));
const Digitizing = lazy(() => import("@/pages/digitizing"));
const Receiving = lazy(() => import("@/pages/receiving"));
const Packing = lazy(() => import("@/pages/packing"));
const Duffs = lazy(() => import("@/pages/duffs"));
const QcSaves = lazy(() => import("@/pages/qc-saves"));

// Mobile nav uses the same items + filtering as the sidebar
const mobileNavItems = sidebarNavItems;

/**
 * Context-aware navigation arrows.
 * Always follows the user's personal browser history.
 */
function NavArrows() {
  const [{ canGoBack, canGoForward }, setHistoryState] = useState(getAppHistoryState);
  const isProgrammatic = useRef(false);

  useEffect(() => {
    setHistoryState(initializeAppHistory());

    const handlePopState = () => {
      if (isProgrammatic.current) {
        isProgrammatic.current = false;
        return;
      }

      setHistoryState(recordAppPop());
    };

    window.addEventListener("popstate", handlePopState);

    const origPush = window.history.pushState.bind(window.history);
    const origReplace = window.history.replaceState.bind(window.history);

    window.history.pushState = function (...args) {
      origPush(...args);
      setHistoryState(recordAppPush());
    };

    window.history.replaceState = function (...args) {
      origReplace(...args);
      setHistoryState(recordAppReplace());
    };

    return () => {
      window.removeEventListener("popstate", handlePopState);
      window.history.pushState = origPush;
      window.history.replaceState = origReplace;
    };
  }, []);

  const brandColor = "#00363A";

  return (
    <div className="flex items-center gap-1" data-testid="nav-arrows">
      <button
        onClick={() => { if (canGoBack) { isProgrammatic.current = true; setHistoryState(recordAppBack()); window.history.back(); } }}
        className="h-8 w-8 flex items-center justify-center rounded-md hover:bg-muted/60 transition-colors"
        style={{ color: brandColor, opacity: canGoBack ? 1 : 0.35 }}
        aria-label="Go back"
        data-testid="nav-back"
      >
        <ChevronLeft className="h-5 w-5" />
      </button>
      <button
        onClick={() => { if (canGoForward) { isProgrammatic.current = true; setHistoryState(recordAppForward()); window.history.forward(); } }}
        className="h-8 w-8 flex items-center justify-center rounded-md hover:bg-muted/60 transition-colors"
        style={{ color: brandColor, opacity: canGoForward ? 1 : 0.35 }}
        aria-label="Go forward"
        data-testid="nav-forward"
      >
        <ChevronRight className="h-5 w-5" />
      </button>
    </div>
  );
}

function NotificationBell() {
  const { user } = useUser();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const [, navigate] = useLocation();

  const { data: countData } = useQuery<{ count: number }>({
    ...qcNotificationQuery<{ count: number }>(["/api/notifications/unread-count"], user?.id),
    refetchInterval: 30000,
  });

  const itemsQuery = useQuery<Notification[]>({
    ...qcNotificationQuery<Notification[]>(["/api/notifications"], user?.id),
    enabled: open && Boolean(user?.id),
    // An open bell must notice work-order access revoked by another user.
    refetchInterval: 15_000,
    refetchIntervalInBackground: true,
  });
  const items = readableNotificationData(itemsQuery);
  const itemsLoading = itemsQuery.isPending || itemsQuery.fetchStatus !== "idle";

  const markRead = useMutation({
    mutationFn: (id: string) => apiRequest("PATCH", `/api/notifications/${id}/read`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/notifications"] });
      queryClient.invalidateQueries({ queryKey: ["/api/notifications/unread-count"] });
    },
  });

  const markAllRead = useMutation({
    mutationFn: () => apiRequest("POST", "/api/notifications/mark-all-read"),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/notifications"] });
      queryClient.invalidateQueries({ queryKey: ["/api/notifications/unread-count"] });
    },
  });

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    if (open) document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open]);

  const unreadCount = countData?.count ?? 0;

  const handleNotificationClick = (n: Notification) => {
    if (!n.read) markRead.mutate(n.id);
    if (n.workOrderId) {
      navigate(`/work-orders/${n.workOrderId}${n.qcSaveId ? `?qcSave=${encodeURIComponent(n.qcSaveId)}` : ""}`);
      setOpen(false);
    }
  };

  const formatTime = (dateStr: string | Date) => {
    const d = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - d.getTime();
    const diffMin = Math.floor(diffMs / 60000);
    if (diffMin < 1) return "just now";
    if (diffMin < 60) return `${diffMin}m ago`;
    const diffH = Math.floor(diffMin / 60);
    if (diffH < 24) return `${diffH}h ago`;
    const diffD = Math.floor(diffH / 24);
    return `${diffD}d ago`;
  };

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(!open)}
        className="relative p-2 rounded-full transition-colors hover:bg-black/5"
        style={{ color: "hsl(var(--brand-dark))", border: "1.5px solid hsl(var(--border))" }}
        data-testid="button-notifications"
      >
        <Bell className="h-5 w-5" />
        {unreadCount > 0 && (
          <span
            className="absolute -top-0.5 -right-0.5 flex items-center justify-center rounded-full text-[9px] font-bold leading-none"
            style={{
              backgroundColor: "#a4e96c",
              color: "#000000",
              minWidth: 16,
              height: 16,
              padding: "0 4px",
            }}
            data-testid="badge-notification-count"
          >
            {unreadCount > 99 ? "99+" : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div
          className="fixed sm:absolute right-2 sm:right-0 mt-1 w-[calc(100vw-1rem)] sm:w-[30rem] rounded-md shadow-lg overflow-hidden"
          style={{ zIndex: 10000, backgroundColor: "#ffffff", border: "1px solid hsl(var(--brand-dark))" }}
          data-testid="notification-panel"
        >
          <div className="flex items-center justify-between px-3 py-2" style={{ borderBottom: "1px solid #e5e7eb" }}>
            <span className="text-xs font-semibold uppercase tracking-wider" style={{ color: "hsl(var(--brand-dark))" }}>Notifications</span>
            {unreadCount > 0 && (
              <button
                onClick={() => markAllRead.mutate()}
                className="flex items-center gap-1 text-[10px] font-medium"
                style={{ color: "hsl(var(--primary))" }}
                data-testid="button-mark-all-read"
              >
                <CheckCheck className="h-3 w-3" />
                Mark all read
              </button>
            )}
          </div>
          <div className="max-h-80 overflow-y-auto">
            {itemsQuery.fetchStatus === "paused" ? (
              <div className="p-6 text-center">
                <p role="status" className="text-xs" style={{ color: "#6b7280" }}>Reconnect to check your notifications.</p>
              </div>
            ) : itemsLoading ? (
              <div className="p-6 text-center">
                <p className="text-xs" style={{ color: "#6b7280" }}>Loading...</p>
              </div>
            ) : itemsQuery.isError ? (
              <div className="p-6 text-center" role="alert">
                <p className="text-xs">Notifications could not be loaded.</p>
                <Button variant="outline" size="sm" onClick={() => itemsQuery.refetch()}>Retry</Button>
              </div>
            ) : !items || items.length === 0 ? (
              <div className="p-6 text-center">
                <Bell className="h-8 w-8 mx-auto mb-2" style={{ color: "#9ca3af" }} />
                <p className="text-xs" style={{ color: "#6b7280" }}>No notifications yet</p>
              </div>
            ) : (
              items.map((n) => (
                <button
                  key={n.id}
                  onClick={() => handleNotificationClick(n)}
                  className="w-full text-left px-3 py-2.5 flex items-start gap-2.5 transition-colors"
                  style={{
                    borderBottom: "1px solid #e5e7eb",
                    backgroundColor: n.read ? "transparent" : "rgba(0, 124, 127, 0.06)",
                  }}
                  data-testid={`notification-item-${n.id}`}
                >
                  <div
                    className="shrink-0 mt-0.5 h-6 w-6 rounded-full flex items-center justify-center"
                    style={{
                      backgroundColor: n.type === "mention" ? "rgba(0, 124, 127, 0.12)" : "rgba(0, 54, 56, 0.08)",
                    }}
                  >
                    {n.type === "mention" ? (
                      <AtSign className="h-3 w-3" style={{ color: "hsl(var(--primary))" }} />
                    ) : (
                      <Zap className="h-3 w-3" style={{ color: "hsl(var(--primary))" }} />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs leading-snug" style={{ color: n.read ? "#6b7280" : "hsl(var(--brand-dark))" }}>
                      {n.message}
                    </p>
                    <p className="text-[10px] mt-0.5" style={{ color: "#9ca3af" }}>
                      {formatTime(n.createdAt)}
                    </p>
                  </div>
                  {!n.read && (
                    <div className="shrink-0 mt-1.5 h-2 w-2 rounded-full" style={{ backgroundColor: "hsl(var(--primary))" }} />
                  )}
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function MobileMenu() {
  const [open, setOpen] = useState(false);
  const [bugReportOpen, setBugReportOpen] = useState(false);
  const [location] = useLocation();
  const { isAdmin, isManager, isSuperAdmin, specialties, allSpecialties, logout, isLoggingOut } = useUser();

  useEffect(() => {
    setOpen(false);
  }, [location]);

  return (
    <div className="md:hidden z-50 pr-2">
      <button
        onClick={() => setOpen(!open)}
        className="p-2"
        style={{ color: "hsl(var(--brand-dark))" }}
        data-testid="button-mobile-menu"
      >
        {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
      </button>

      {open && (
        <>
          <div
            className="fixed inset-0 bg-black/40"
            onClick={() => setOpen(false)}
            style={{ top: 44, zIndex: 9998 }}
          />
          <nav
            className="fixed right-0 min-w-[200px] py-2 rounded-bl-md shadow-lg"
            style={{ top: 44, zIndex: 9999, backgroundColor: "hsl(var(--brand-dark))", border: "1px solid rgba(164, 233, 108, 0.3)", borderTop: "none" }}
            data-testid="mobile-nav-dropdown"
          >
            {mobileNavItems.filter((item) => isNavItemVisible(item, isAdmin, isManager, isSuperAdmin, specialties, allSpecialties)).map((item) => {
              const isActive = item.url === "/" ? location === "/" : location.startsWith(item.url);
              return (
                <Link
                  key={item.title}
                  href={item.url}
                  className="flex items-center gap-3 px-4 py-2.5"
                  style={{
                    color: isActive ? "hsl(var(--brand-green))" : "rgba(255,255,255,0.85)",
                    backgroundColor: isActive ? "rgba(164, 233, 108, 0.1)" : "transparent",
                  }}
                  data-testid={`mobile-nav-${item.title.toLowerCase().replace(/\s/g, "-")}`}
                >
                  <item.icon className="h-4 w-4 shrink-0" />
                  <span className="text-sm font-medium">{item.title}</span>
                </Link>
              );
            })}
            <button
              onClick={() => { setOpen(false); setBugReportOpen(true); }}
              className="flex items-center gap-3 px-4 py-2.5 w-full text-left"
              style={{ color: "rgba(255,255,255,0.85)", borderTop: "1px solid rgba(255,255,255,0.15)" }}
              data-testid="mobile-nav-bug-report"
            >
              <Bug className="h-4 w-4 shrink-0" />
              <span className="text-sm font-medium">Report a Bug</span>
            </button>
            <button
              onClick={logout}
              disabled={isLoggingOut}
              aria-busy={isLoggingOut}
              className="flex items-center gap-3 px-4 py-2.5 w-full text-left"
              style={{ color: "rgba(255,255,255,0.85)" }}
              data-testid="mobile-nav-logout"
            >
              <LogOut className="h-4 w-4 shrink-0" />
              <span className="text-sm font-medium">{isLoggingOut ? "Signing out…" : "Sign Out"}</span>
            </button>
          </nav>
        </>
      )}
      <BugReportDialog open={bugReportOpen} onOpenChange={setBugReportOpen} />
    </div>
  );
}

function AdminRoute({ component: Component }: { component: React.ComponentType }) {
  const { isAdmin, isRoleLoaded } = useUser();
  const [, navigate] = useLocation();

  useEffect(() => {
    if (isRoleLoaded && !isAdmin) {
      navigate("/", { replace: true });
    }
  }, [isAdmin, isRoleLoaded, navigate]);

  if (!isRoleLoaded) return null;
  if (!isAdmin) return null;
  return <Component />;
}

function SpecialtyRoute({ component: Component, allowedSpecialties }: { component: React.ComponentType; allowedSpecialties: string[] }) {
  const { isAdmin, isManager, isSuperAdmin, specialties, isRoleLoaded } = useUser();
  const [, navigate] = useLocation();
  // HUB-501 (PR #312 review): super admins pass, as they do on the server (`requireSpecialty`) and in the
  // sidebar, which lists every specialty page for them; without this the sidebar link bounced them home.
  const allowed = isAdmin || isManager || isSuperAdmin || specialties.some(s => allowedSpecialties.includes(s));

  useEffect(() => {
    if (isRoleLoaded && !allowed) navigate("/", { replace: true });
  }, [isRoleLoaded, allowed, navigate]);

  if (!isRoleLoaded) return null;
  if (!allowed) return null;
  return <Component />;
}

function LazyRoute({ component: Component }: { component: React.LazyExoticComponent<React.ComponentType<any>> }) {
  return (
    <ErrorBoundary>
      <Suspense fallback={
        <div className="flex items-center justify-center h-64">
          <div className="h-1 w-24 rounded-full overflow-hidden" style={{ backgroundColor: "rgba(0, 124, 127, 0.2)" }}>
            <div className="h-full rounded-full animate-pulse" style={{ backgroundColor: "hsl(var(--primary))", width: "60%" }} />
          </div>
        </div>
      }>
        <Component />
      </Suspense>
    </ErrorBoundary>
  );
}

function Router() {
  return (
    <Switch>
      <Route path="/">{() => <LazyRoute component={Dashboard} />}</Route>
      <Route path="/dashboard">{() => <Redirect to="/" />}</Route>
      <Route path="/work-orders">{() => <LazyRoute component={WorkOrders} />}</Route>
      <Route path="/work-orders/:id">{() => <LazyRoute component={WorkOrderDetail} />}</Route>
      <Route path="/prep-steps">{() => <Redirect to="/" />}</Route>
      <Route path="/schedule">{() => <ErrorBoundary><Suspense fallback={null}><SpecialtyRoute component={Schedule} allowedSpecialties={SPECIALTY_PAGE_ACCESS["/schedule"]!} /></Suspense></ErrorBoundary>}</Route>
      <Route path="/lanes">{() => <ErrorBoundary><Suspense fallback={null}><SpecialtyRoute component={Lanes} allowedSpecialties={SPECIALTY_PAGE_ACCESS["/lanes"]!} /></Suspense></ErrorBoundary>}</Route>
      <Route path="/machines">{() => <ErrorBoundary><Suspense fallback={null}><AdminRoute component={Machines} /></Suspense></ErrorBoundary>}</Route>
      <Route path="/screenmaster">{() => <ErrorBoundary><Suspense fallback={null}><SpecialtyRoute component={ScreenMaster} allowedSpecialties={SPECIALTY_PAGE_ACCESS["/screenmaster"]!} /></Suspense></ErrorBoundary>}</Route>
      <Route path="/inkmaster">{() => <ErrorBoundary><Suspense fallback={null}><SpecialtyRoute component={InkMaster} allowedSpecialties={SPECIALTY_PAGE_ACCESS["/inkmaster"]!} /></Suspense></ErrorBoundary>}</Route>
      <Route path="/digitizing">{() => <ErrorBoundary><Suspense fallback={null}><SpecialtyRoute component={Digitizing} allowedSpecialties={["digitizer"]} /></Suspense></ErrorBoundary>}</Route>
      <Route path="/shipping-receiving">{() => <Redirect to="/receiving" />}</Route>
      <Route path="/receiving">{() => <ErrorBoundary><Suspense fallback={null}><SpecialtyRoute component={Receiving} allowedSpecialties={SPECIALTY_PAGE_ACCESS["/receiving"]!} /></Suspense></ErrorBoundary>}</Route>
      <Route path="/packing">{() => <ErrorBoundary><Suspense fallback={null}><SpecialtyRoute component={Packing} allowedSpecialties={SPECIALTY_PAGE_ACCESS["/packing"]!} /></Suspense></ErrorBoundary>}</Route>
      <Route path="/duffs">{() => <LazyRoute component={Duffs} />}</Route>
      <Route path="/qc-saves">{() => <LazyRoute component={QcSaves} />}</Route>
      <Route path="/team">{() => <ErrorBoundary><Suspense fallback={null}><AdminRoute component={Team} /></Suspense></ErrorBoundary>}</Route>
      <Route path="/settings">{() => <ErrorBoundary><Suspense fallback={null}><AdminRoute component={SettingsPage} /></Suspense></ErrorBoundary>}</Route>
      <Route path="/webhook-fields">{() => <ErrorBoundary><Suspense fallback={null}><AdminRoute component={WebhookFields} /></Suspense></ErrorBoundary>}</Route>
      <Route component={NotFound} />
    </Switch>
  );
}

function AuthenticatedApp() {
  const [commandOpen, setCommandOpen] = useState(false);

  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        setCommandOpen((prev) => !prev);
      }
    }
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, []);

  const style = {
    "--sidebar-width": "14.4rem",
    "--sidebar-width-icon": "3rem",
  };

  return (
    <SidebarProvider style={style as React.CSSProperties}>
      <div className="flex h-screen w-full">
        <AppSidebar />
        <div className="flex flex-col flex-1 min-w-0 relative">
          <ImpersonationBanner />
          <ReprintAlertBanner />
          <main className="pp-hub-canvas flex-1 overflow-hidden flex flex-col">
            {/* Top toolbar — back/forward nav + notification bell + mobile menu */}
            <div className="pp-hub-toolbar flex items-center justify-between px-2 py-1 shrink-0" data-testid="top-toolbar">
              <NavArrows />
              <button
                onClick={() => setCommandOpen(true)}
                className="pp-hub-search flex items-center gap-2 px-3 py-1.5 rounded-md border text-sm text-muted-foreground hover:text-foreground hover:bg-accent transition-colors max-w-xs"
                data-testid="button-global-search"
              >
                <Search className="h-3.5 w-3.5 shrink-0" />
                <span className="hidden sm:inline truncate">Search orders...</span>
                <kbd className="hidden md:inline-flex pointer-events-none h-5 select-none items-center gap-1 rounded border bg-muted px-1.5 font-mono text-[10px] font-medium text-muted-foreground ml-auto">
                  <span className="text-xs">⌘</span>K
                </kbd>
              </button>
              <div className="flex items-center gap-1" data-testid="header-actions">
                <NotificationBell />
                <MobileMenu />
              </div>
            </div>
            {/* Scrollable page content */}
            <div className="flex-1 overflow-y-auto">
              <ErrorBoundary>
                <Router />
              </ErrorBoundary>
            </div>
          </main>
        </div>
      </div>
      <CommandSearch open={commandOpen} onOpenChange={setCommandOpen} />
    </SidebarProvider>
  );
}

function PasswordExpiryWarning() {
  const { user } = useUser();
  const [showDialog, setShowDialog] = useState(false);

  if (!user?.passwordExpiryDays || user.passwordExpiryDays > 30) return null;

  const days = user.passwordExpiryDays;
  const label = days <= 0
    ? "Your password has expired"
    : `Your password expires in ${days} day${days === 1 ? "" : "s"}`;

  return (
    <>
      <div className="bg-amber-50 dark:bg-amber-950/40 border-b border-amber-200 dark:border-amber-800 px-4 py-2 flex items-center justify-between text-sm">
        <span className="text-amber-800 dark:text-amber-200 font-medium">{label}</span>
        <Button size="sm" variant="outline" className="h-7 text-xs border-amber-300 hover:bg-amber-100 dark:border-amber-700 dark:hover:bg-amber-900/40" onClick={() => setShowDialog(true)}>
          Change Now
        </Button>
      </div>
      <ChangePasswordDialog open={showDialog} onOpenChange={setShowDialog} />
    </>
  );
}

function OfflineActionRecovery() {
  const { user, isLoggingOut, isIdentityUnconfirmed, isCheckingIdentity } = useUser();
  const identityHeld = isIdentityUnconfirmed || isCheckingIdentity;
  useEffect(() => {
    // Releasing the hold re-runs this effect, which resumes the work that was kept paused.
    if (user && !isLoggingOut && !identityHeld) void resumePausedShopFloorMutations(queryClient);
  }, [user?.id, isLoggingOut, identityHeld]);
  return null;
}

function AppContent() {
  const { user, isAuthenticated, isLoading } = useUser();

  if (isLoading) {
    return (
      <div className="h-screen flex items-center justify-center" style={{ backgroundColor: "hsl(var(--brand-dark))" }}>
        <div className="flex flex-col items-center gap-3">
          <img src={productionHubLogo} alt="Prodigy Production Hub" style={{ height: "3.39rem" }} />
          <div className="h-1 w-24 rounded-full overflow-hidden mt-2" style={{ backgroundColor: "rgba(164, 233, 108, 0.2)" }}>
            <div className="h-full rounded-full animate-pulse" style={{ backgroundColor: "hsl(var(--brand-green))", width: "60%" }} />
          </div>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    // Allow reset-password page when not authenticated
    if (window.location.pathname === "/reset-password") {
      return <ResetPassword />;
    }
    return <Login />;
  }

  return (
    <Fragment key={user?.id}>
      <ChangePasswordDialog forced />
      <PasswordExpiryWarning />
      <AuthenticatedApp />
    </Fragment>
  );
}

function App() {
  const shopFloorPersister = createShopFloorPersister();

  useEffect(() => {
    const resume = () => {
      void resumePausedShopFloorMutations(queryClient);
    };

    window.addEventListener("online", resume);
    window.addEventListener("focus", resume);
    return () => { window.removeEventListener("online", resume); window.removeEventListener("focus", resume); };
  }, []);

  return (
    <PersistQueryClientProvider
      client={queryClient}
      persistOptions={{
        persister: shopFloorPersister,
        buster: SHOP_FLOOR_PERSIST_BUSTER,
        dehydrateOptions: {
          shouldDehydrateQuery: () => false,
          shouldDehydrateMutation: (mutation) => mutation.state.isPaused,
        },
      }}
      onSuccess={() => resumePausedShopFloorMutations(queryClient)}
    >
      <TooltipProvider>
        <UserProvider>
          <OfflineActionRecovery />
          <AppContent />
          <Toaster />
          <IdentityRecovery />
        </UserProvider>
      </TooltipProvider>
    </PersistQueryClientProvider>
  );
}

export default App;
